'use client';

import { useState, useMemo, useEffect, useRef, isValidElement, type ReactNode } from 'react';
import { Hero, type HeroCarouselHero } from './Hero';
import cn from 'classnames';
import styles from './herocarousel.module.scss';

export interface HeroCarouselProps {
    heroes: HeroCarouselHero[];
    autoScrollTime?: number;
    thumbnailVariant: 'Square' | 'Round';
}

export function HeroCarousel({
    heroes,
    autoScrollTime = 0,
    thumbnailVariant = 'Round',
}: HeroCarouselProps): React.JSX.Element {
    const heightHeroRef = useRef<HTMLDivElement>(null);
    const [activeHero, setActiveHero] = useState(0);
    const [preloadAll, setPreloadAll] = useState(false);
    const [hiddenHero, setHiddenHero] = useState(0);
    const [height, setHeight] = useState(0);

    useEffect(() => {
        // Preload all slide assets when the browser is idle to avoid competing with initial render.
        setPreloadAll(false);
        const win = window as typeof window & {
            requestIdleCallback?: (callback: () => void) => number;
            cancelIdleCallback?: (handle: number) => void;
        };
        const schedule = win.requestIdleCallback || ((cb: () => void) => window.setTimeout(cb, 1));
        const cancel = win.cancelIdleCallback || ((handle: number) => window.clearTimeout(handle));
        const handle = schedule(() => setPreloadAll(true));

        return () => cancel(handle);
    }, [heroes]);

    const preloadUrls = useMemo(() => {
        const urls = new Set<string>();

        const addElementSrc = (node?: ReactNode) => {
            if (isValidElement(node)) {
                const src = (node.props as { src?: string }).src;
                if (typeof src === 'string' && src.length > 0) {
                    urls.add(src);
                }
            }
        };

        if (heroes.length > 0) {
            // Preload non-active slides only when idle; the active slide loads at higher priority in the hero.
            const indices = preloadAll ? heroes.map((_, index) => index).filter((index) => index !== activeHero) : [];

            indices.forEach((index) => {
                const hero = heroes[index];
                if (!hero) return;
                if (hero.backgroundImage) {
                    urls.add(hero.backgroundImage);
                }
                if (hero.backgroundImageMobile) {
                    urls.add(hero.backgroundImageMobile);
                }
                addElementSrc(hero.foregroundImage);
                addElementSrc(hero.foregroundImageMobile);
            });
        }

        return Array.from(urls);
    }, [heroes, activeHero, preloadAll]);

    const slideButtons = useMemo(() => {
        return heroes.map((hero, i) => {
            const buttonClass = cn(styles.carouselButton, styles[thumbnailVariant], {
                [styles.active]: i === activeHero,
            });

            const linkText = `hero-${i}-${hero.name || ''}`;

            return (
                <button key={`heroButton${i}`} data-tracklinktext={linkText} className={buttonClass} onClick={() => setActiveHero(i)}>
                    {hero.carouselImage}
                </button>
            );
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeHero]);

    useEffect(() => {
        let heroTimeout: NodeJS.Timeout | undefined;

        if (autoScrollTime) {
            const newIndex = activeHero + 1 >= heroes.length ? 0 : activeHero + 1;
            heroTimeout = setTimeout(() => setActiveHero(newIndex), autoScrollTime * 1000);
        }

        return () => {
            if (heroTimeout) {
                clearTimeout(heroTimeout);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeHero]);

    useEffect(() => {
        if (heightHeroRef.current) {
            if (hiddenHero < heroes.length) {
                if (heightHeroRef.current.clientHeight > height) {
                    setHeight(heightHeroRef.current?.clientHeight);
                }
            }

            setHiddenHero(hiddenHero + 1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [heightHeroRef.current, hiddenHero]);

    return (
        <>
            <Hero
                {...heroes[activeHero]}
                slideButtons={slideButtons}
                height={hiddenHero >= heroes.length ? `${height}px` : 'auto'}
            />

            {preloadUrls.length > 0 && (
                <div className={styles.preload} aria-hidden>
                    {/* Render after the main hero so the visible slide assets are requested first. */}
                    {preloadUrls.map((src) => (
                        <img key={src} src={src} alt="" loading="eager" fetchPriority="low" />
                    ))}
                </div>
            )}

            {hiddenHero < heroes.length && (
                <div ref={heightHeroRef} className={styles.offscreen}>
                    <Hero {...heroes[hiddenHero]} backgroundImage={undefined} slideButtons={slideButtons} />
                </div>
            )}
        </>
    );
}
